import React, { useEffect, useState } from 'react'
import CustomLinearProgress from './CustomLinearProgress'
import classnames from 'classnames'
import FilterIcon from '../../common/icons/FilterIcon'
import SortIcon from '../../common/icons/SortIcon'
import IncomeFilterPopup from './IncomeFilterPopup'
import ProBadge from '../../common/ProBadge'
import CloseIcon from '../../common/icons/CloseIcon'
import { ExpandIcon, ContractIcon } from './Icons'

const IncomeSection = ({
  showIncomePolygonData,
  showIndividualPolygonLines,
  safeIncomeProgressData,
  polygonColors,
  safeAreaIncomeValue,
  safeTotalPopulation,
  aggSelector,
  incomeSelectedYear,
  polygonData,
  singlePolygonData,
  rightSidebarStore,
  uiStore,
  isExpanded,
  onToggleExpand
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isSortOpen, setIsSortOpen] = useState(false)
  const [localSortDir, setLocalSortDir] = useState('asc')

  const getPolygonPopulationForYear = React.useCallback((polygonId) => {
    if (!polygonData || polygonData.length === 0 || polygonId == null) return 0
    const selectedYearNum = typeof incomeSelectedYear === 'string'
      ? parseInt(incomeSelectedYear, 10)
      : incomeSelectedYear
    const polygon = polygonData.find(p => String(p.id) === String(polygonId))
    if (!polygon || !polygon.trends || !selectedYearNum) return 0
    const trend = polygon.trends.find(t => String(t.year) === String(selectedYearNum))
    return trend ? (trend.population || 0) : 0
  }, [polygonData, incomeSelectedYear])

  const maxChartIncome = React.useMemo(() => {
    if (!safeIncomeProgressData || safeIncomeProgressData.length === 0) return 1
    const max = Math.max(...safeIncomeProgressData.map(item => item.maxIncome || item.income || 0))
    return max > 0 ? Math.ceil(max) : 1
  }, [safeIncomeProgressData])

  const maxChartPopulation = React.useMemo(() => {
    return safeTotalPopulation > 0 ? safeTotalPopulation : 1
  }, [safeTotalPopulation])

  const [populationRange, setPopulationRange] = useState([0, maxChartPopulation])
  const [incomeRange, setIncomeRange] = useState([0, maxChartIncome])

  // Both income and population max values as well as ranges need to be properly set
  useEffect(() => {
    setPopulationRange([0, maxChartPopulation])
  }, [maxChartPopulation])

  useEffect(() => {
    setIncomeRange([0, maxChartIncome || 0])
  }, [maxChartIncome])

  const sortedIncomeProgressData = [...safeIncomeProgressData].sort((a, b) => {
    if (!a.name || !b.name) return 0
    const nameA = String(a.name).toLowerCase()
    const nameB = String(b.name).toLowerCase()
    return localSortDir === 'asc' ? (nameA > nameB ? 1 : -1) : (nameA < nameB ? 1 : -1)
  })

  const filteredIncomeProgressData = sortedIncomeProgressData.filter((item) => {
    const income = item.income || 0
    const population = getPolygonPopulationForYear(item.id)
    const [popMin, popMax] = populationRange
    const [incMin, incMax] = incomeRange
    return (
      population >= popMin &&
      population <= popMax &&
      income >= incMin &&
      income <= incMax
    )
  })
  const shouldCenterExpandedContent = isExpanded && !singlePolygonData && !(showIncomePolygonData && showIndividualPolygonLines && sortedIncomeProgressData.length > 0)

  return (
    <div className={`income-data-sidebar__section income-data-sidebar__section--income ${isExpanded ? 'income-data-sidebar__section--expanded-chart' : ''}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 className='income-data-sidebar__section-title' style={{ margin: 0, paddingBottom: 0 }}>Income</h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {(!singlePolygonData && showIncomePolygonData && showIndividualPolygonLines && safeIncomeProgressData.length > 0) && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <div className='item-controls__filter-holder' style={{ position: 'relative' }}>
                <button
                  type='button'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    borderRadius: '4px',
                    border: isFilterOpen ? '1px solid #10b981' : '1px solid #e5e7eb',
                    backgroundColor: isFilterOpen ? '#f0fdf4' : 'transparent',
                    color: isFilterOpen ? '#10b981' : '#6b7280',
                    cursor: 'pointer'
                  }}
                  className={classnames({ 'item-controls__disabled': uiStore && uiStore.user && uiStore.user.isStarter })}
                  onClick={() => {
                    setIsFilterOpen(!isFilterOpen)
                    setIsSortOpen(false)
                  }}
                >
                  <FilterIcon />
                  {uiStore && uiStore.user && uiStore.user.isStarter && <ProBadge className='map-settings__pro text-small' />}
                </button>
                {isFilterOpen && (
                  <div
                    className='trends-filter-override'
                    style={{ position: 'absolute', top: '100%', right: '-80px', zIndex: 1000 }}
                  >
                    <style>{`
                    .trends-filter-override .filter {
                      position: relative !important;
                      left: auto !important;
                      right: 0 !important;
                      margin-top: 8px;
                      max-height: 80vh;
                      overflow-x: hidden;
                      overflow-y: auto;
                      width: 300px !important;
                      max-width: 300px !important;
                      box-sizing: border-box !important;
                    }
                    .trends-filter-override .filter .range-slider,
                    .trends-filter-override .filter .filter__range-slider {
                      width: 100% !important;
                      box-sizing: border-box !important;
                    }
                    .trends-filter-override .filter__trans-label {
                      width: 100% !important;
                      display: block !important;
                    }
                    `}
                    </style>
                    <IncomeFilterPopup
                      populationRange={populationRange}
                      populationMax={maxChartPopulation}
                      onPopulationChange={setPopulationRange}
                      incomeRange={incomeRange}
                      incomeMax={maxChartIncome}
                      onIncomeChange={setIncomeRange}
                      onClose={() => setIsFilterOpen(false)}
                      onApply={() => setIsFilterOpen(false)}
                      onReset={() => {
                        setPopulationRange([0, maxChartPopulation])
                        setIncomeRange([0, maxChartIncome])
                      }}
                    />
                  </div>
                )}
              </div>

              <div className='item-controls__sort-holder' style={{ position: 'relative' }}>
                <button
                  type='button'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    borderRadius: '4px',
                    border: isSortOpen ? '1px solid #10b981' : '1px solid #e5e7eb',
                    backgroundColor: isSortOpen ? '#f0fdf4' : 'transparent',
                    color: isSortOpen ? '#10b981' : '#6b7280',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setIsSortOpen(!isSortOpen)
                    setIsFilterOpen(false)
                  }}
                >
                  <SortIcon />
                </button>
                {isSortOpen && (
                  <div
                    className='trends-sort-override'
                    style={{ position: 'absolute', top: '100%', right: '0', zIndex: 1000 }}
                  >
                    <style>{`
                    .trends-sort-override .choose-add-type {
                      --left-position: auto !important;
                      left: auto !important;
                      right: 0 !important;
                      margin-top: 8px;
                    }
                    `}
                    </style>
                    <div className='choose-add-type'>
                      <button
                        type='button'
                        className={classnames({ 'choose-add-type__btn': true, 'choose-add-type__btn--sort': true, selected: localSortDir === 'asc' })}
                        onClick={() => {
                          setLocalSortDir('asc')
                          setIsSortOpen(false)
                        }}
                      >
                        A to Z
                      </button>
                      <button
                        type='button'
                        className={classnames({ 'choose-add-type__btn': true, 'choose-add-type__btn--sort': true, selected: localSortDir === 'desc' })}
                        onClick={() => {
                          setLocalSortDir('desc')
                          setIsSortOpen(false)
                        }}
                      >
                        Z to A
                      </button>
                      <button type='button' className='choose-add-type__close-btn' onClick={() => setIsSortOpen(false)}>
                        <CloseIcon />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <button
            type='button'
            className='income-data-sidebar__expand-btn'
            onClick={onToggleExpand}
            aria-label={isExpanded ? 'Contract income chart' : 'Expand income chart'}
          >
            {isExpanded ? <ContractIcon /> : <ExpandIcon />}
          </button>
        </div>
      </div>
      <div
        style={
          shouldCenterExpandedContent
            ? { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }
            : undefined
        }
      >
        {!singlePolygonData && showIncomePolygonData && showIndividualPolygonLines && sortedIncomeProgressData.length > 0 ? (
          filteredIncomeProgressData.length > 0 ? (
            filteredIncomeProgressData.map((item, index) => {
              const color = item.color || polygonColors[index % polygonColors.length]
              return (
                <CustomLinearProgress
                  key={item.id}
                  label={item.name && item.name !== 'N/A' && item.name !== 'null' ? item.name : (rightSidebarStore ? rightSidebarStore.resolveShapeName(item, index, rightSidebarStore.targetGroupId) : `Polygon ${index + 1}`)}
                  value={item.income}
                  max={item.maxIncome}
                  color={color}
                  onClick={() => { }}
                  onHover={() => {
                    if (uiStore && item.id) {
                      uiStore.highlightPolygonFromChart(item.id, color)
                    }
                  }}
                  onExit={() => {
                    if (uiStore && item.id) {
                      uiStore.unhighlightPolygonFromChart(item.id)
                    }
                  }}
                />
              )
            })
          ) : (
            <div className='income-data-sidebar__target-group'>
              <div className='income-data-sidebar__target-group-label'>No shapes match the filter</div>
            </div>
          )
        ) : sortedIncomeProgressData.length > 0 ? (
          sortedIncomeProgressData.map((item) => {
            return (
              <CustomLinearProgress
                key={item.id}
                label={item.name}
                value={item.income}
                max={item.maxIncome}
                color='#648E62'
                onClick={() => {
                  if (aggSelector !== 'dissolved' && !singlePolygonData) {
                    rightSidebarStore.showIncomeIndividualShapes()
                  }
                }}
              />
            )
          })
        ) : (
          <div className='income-data-sidebar__target-group'>
            <div className='income-data-sidebar__target-group-label'>No data available</div>
          </div>
        )}
      </div>
    </div>
  )
}

export default IncomeSection
